// src/components/whatsapp/WhatsAppContactsPage.tsx
// Track E5 — WhatsApp contacts directory (backed by mcp_django B3).
//
// Searchable + filterable table with inline opt-in / block toggles and
// a create drawer. Tag filter is a simple contains check on the JSON
// tags array server-side.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Ban, Check, Contact as ContactIcon, Loader2, Plus, RefreshCw, Search,
  Tag as TagIcon, X, ShieldOff,
} from 'lucide-react';

import {
  ApiError,
  createContact, listContacts, updateContact,
  type ContactIn, type WhatsAppContact,
} from '@/lib/api_whatsapp';

type Notifier = (n: {
  type: 'success' | 'error' | 'info';
  title: string; message?: string; duration?: number;
}) => void;

interface Props {
  addNotification?: Notifier;
  /** Dentro de la app WhatsApp: la app pinta el título; aquí solo las acciones. */
  embedded?: boolean;
}

export function WhatsAppContactsPage({ addNotification, embedded = false }: Props) {
  const [rows, setRows] = useState<WhatsAppContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [optedInFilter, setOptedInFilter] = useState<'' | 'true' | 'false'>('');
  const [pending, setPending] = useState<Record<number, true>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const out = await listContacts({
        q: search || undefined,
        tag: tagFilter || undefined,
        opted_in: optedInFilter === '' ? undefined : optedInFilter === 'true',
        limit: 100,
      });
      setRows(out.items);
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : e?.message || 'Error';
      addNotification?.({ type: 'error', title: 'No se pudieron cargar contactos', message: msg });
    } finally {
      setLoading(false);
    }
  }, [search, tagFilter, optedInFilter, addNotification]);

  useEffect(() => {
    const t = setTimeout(load, 250); // debounce search
    return () => clearTimeout(t);
  }, [load]);

  const allTags = useMemo(() => {
    const s = new Set<string>();
    rows.forEach((r) => r.tags.forEach((t) => s.add(t)));
    return Array.from(s).sort();
  }, [rows]);

  const togglePatch = async (c: WhatsAppContact, patch: Partial<WhatsAppContact>) => {
    setPending((p) => ({ ...p, [c.id]: true }));
    try {
      const updated = await updateContact(c.id, patch);
      setRows((r) => r.map((x) => (x.id === c.id ? updated : x)));
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : e?.message || 'Error';
      addNotification?.({ type: 'error', title: 'No se pudo actualizar', message: msg });
    } finally {
      setPending((p) => { const n = { ...p }; delete n[c.id]; return n; });
    }
  };

  return (
    <div className="space-y-6">
      <div className={`flex flex-wrap items-center gap-3 ${embedded ? 'justify-end' : 'justify-between'}`}>
        {!embedded && (
        <div>
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <ContactIcon className="wa-accent-text" />
            Contactos de WhatsApp
          </h2>
          <p className="text-muted-foreground text-sm mt-1">
            Tus contactos de WhatsApp, con su permiso para recibir mensajes y sus etiquetas para los envíos masivos.
          </p>
        </div>
        )}
        <div className="flex flex-wrap gap-2">
          <button onClick={load} disabled={loading} className="inline-flex items-center justify-center rounded-lg border border-border bg-card hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))] disabled:opacity-50 px-3 py-2 text-sm flex items-center gap-2">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Actualizar
          </button>
          <button onClick={() => setShowCreate(true)} className="wa-btn inline-flex items-center justify-center rounded-lg font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))] focus-visible:ring-offset-2 focus-visible:ring-offset-background px-3 py-2 text-sm flex items-center gap-2">
            <Plus size={16} /> Nuevo contacto
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Nombre o teléfono"
            aria-label="Buscar por nombre o teléfono"
            className="w-full pl-9 pr-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:border-emerald-500 focus:outline-none text-sm"
          />
        </div>
        <select
          value={optedInFilter}
          onChange={(e) => setOptedInFilter(e.target.value as '' | 'true' | 'false')}
          aria-label="Filtrar por permiso"
          className="flex-1 min-w-[140px] sm:flex-none px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm"
        >
          <option value="">Permiso: todos</option>
          <option value="true">Aceptaron mensajes</option>
          <option value="false">No han aceptado</option>
        </select>
        <select
          value={tagFilter}
          onChange={(e) => setTagFilter(e.target.value)}
          aria-label="Filtrar por etiqueta"
          className="flex-1 min-w-[140px] sm:flex-none px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm"
        >
          <option value="">Etiqueta: todas</option>
          {allTags.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>

      {/* Celular: tarjetas (la tabla no cabe a 390 px) */}
      <ul className="sm:hidden space-y-2" aria-label="Contactos">
        {loading && rows.length === 0 ? (
          <li className="py-8 text-center"><Loader2 className="inline animate-spin wa-accent-text" /></li>
        ) : rows.length === 0 ? (
          <li className="py-10 text-center text-muted-foreground">Sin contactos.</li>
        ) : rows.map((c) => (
          <li key={c.id} className={`rounded-xl border border-border bg-card p-3 ${c.is_blocked ? 'opacity-60' : ''}`}>
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-foreground">{c.display_name || '—'}</div>
                <div className="font-mono text-xs text-muted-foreground">{c.phone_number}</div>
              </div>
              {c.opted_in
                ? <span className="inline-flex shrink-0 items-center gap-1 text-xs wa-accent-text"><Check size={14} /> Aceptó</span>
                : <span className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground"><X size={14} /> No</span>}
            </div>
            {c.tags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {c.tags.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-foreground"><TagIcon size={10} /> {t}</span>
                ))}
              </div>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="mr-auto text-[11.5px] text-muted-foreground">{c.last_message_at ? new Date(c.last_message_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : 'Sin mensajes'}</span>
              <button onClick={() => togglePatch(c, { opted_in: !c.opted_in } as any)} disabled={pending[c.id]}
                className="inline-flex items-center justify-center rounded-lg border border-border bg-card hover:bg-muted px-2.5 py-1.5 text-xs whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))] disabled:opacity-50">
                {c.opted_in ? 'Quitar permiso' : 'Dar permiso'}
              </button>
              <button onClick={() => togglePatch(c, { is_blocked: !c.is_blocked } as any)} disabled={pending[c.id]}
                className={`inline-flex items-center gap-1 rounded-lg border border-border bg-card hover:bg-muted px-2.5 py-1.5 text-xs whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))] disabled:opacity-50 ${c.is_blocked ? '' : 'text-destructive'}`}>
                {c.is_blocked ? <ShieldOff size={12} /> : <Ban size={12} />}
                {c.is_blocked ? 'Desbloquear' : 'Bloquear'}
              </button>
            </div>
          </li>
        ))}
      </ul>

      {/* Tabla (tableta y escritorio) */}
      <div className="hidden sm:block bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-muted-foreground uppercase text-xs">
              <tr>
                <th className="text-left px-4 py-2">Contacto</th>
                <th className="text-left px-4 py-2">Etiquetas</th>
                <th className="text-left px-4 py-2">Permiso</th>
                <th className="text-left px-4 py-2">Último mensaje</th>
                <th className="text-left px-4 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading && rows.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-8"><Loader2 className="inline animate-spin wa-accent-text" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-10 text-muted-foreground">Sin contactos.</td></tr>
              ) : rows.map((c) => (
                <tr key={c.id} className={`hover:bg-muted/40 ${c.is_blocked ? 'opacity-60' : ''}`}>
                  <td className="px-4 py-2">
                    <div className="font-medium text-foreground">{c.display_name || '—'}</div>
                    <div className="text-xs text-muted-foreground font-mono">{c.phone_number}</div>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex gap-1 flex-wrap">
                      {c.tags.length === 0
                        ? <span className="text-xs text-muted-foreground">—</span>
                        : c.tags.map((t) => (
                            <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-xs text-foreground">
                              <TagIcon size={10} /> {t}
                            </span>
                          ))}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    {c.opted_in ? (
                      <span className="inline-flex items-center gap-1 text-xs wa-accent-text">
                        <Check size={14} /> Aceptó
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                        <X size={14} /> No
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {c.last_message_at ? new Date(c.last_message_at).toLocaleString('es-MX') : '—'}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => togglePatch(c, { opted_in: !c.opted_in } as any)}
                        disabled={pending[c.id]}
                        className="inline-flex items-center justify-center rounded-lg border border-border bg-card hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))] disabled:opacity-50 whitespace-nowrap px-2 py-1 text-xs"
                      >
                        {c.opted_in ? 'Quitar permiso' : 'Dar permiso'}
                      </button>
                      <button
                        onClick={() => togglePatch(c, { is_blocked: !c.is_blocked } as any)}
                        disabled={pending[c.id]}
                        className={`inline-flex items-center justify-center rounded-lg border border-border bg-card hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))] disabled:opacity-50 whitespace-nowrap px-2 py-1 text-xs ${c.is_blocked ? '' : 'text-destructive'} flex items-center gap-1`}
                      >
                        {c.is_blocked ? <ShieldOff size={12} /> : <Ban size={12} />}
                        {c.is_blocked ? 'Desbloquear' : 'Bloquear'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showCreate && (
        <CreateContactDrawer
          onClose={() => setShowCreate(false)}
          onCreated={async () => { setShowCreate(false); await load(); }}
          notify={addNotification}
        />
      )}
    </div>
  );
}

function CreateContactDrawer({
  onClose, onCreated, notify,
}: {
  onClose: () => void;
  onCreated: () => void | Promise<void>;
  notify?: Notifier;
}) {
  const [form, setForm] = useState<ContactIn>({
    phone_number: '',
    display_name: '',
    opted_in: true,
    opt_in_source: 'manual',
    tags: [],
  });
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    setForm((f) => ({ ...f, tags: Array.from(new Set([...(f.tags || []), t])) }));
    setTagInput('');
  };

  const removeTag = (tag: string) => {
    setForm((f) => ({ ...f, tags: (f.tags || []).filter((x) => x !== tag) }));
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createContact({ ...form, phone_number: form.phone_number.trim() });
      notify?.({ type: 'success', title: 'Contacto creado' });
      await onCreated();
    } catch (e: any) {
      notify?.({
        type: 'error',
        title: 'No se pudo crear',
        message: e instanceof ApiError ? e.message : e?.message || 'Error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card text-card-foreground rounded-xl border border-border max-w-lg w-full">
        <div className="p-6">
          <h3 className="text-xl font-semibold text-foreground mb-4">Nuevo contacto</h3>
          <form onSubmit={save} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Teléfono con lada (ej. +5215511112222)</label>
              <input
                type="text"
                value={form.phone_number}
                onChange={(e) => setForm((f) => ({ ...f, phone_number: e.target.value }))}
                placeholder="+5215511112222"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:border-emerald-500 focus:outline-none font-mono"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Nombre</label>
              <input
                type="text"
                value={form.display_name || ''}
                onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
                placeholder="Ana López"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:border-emerald-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Etiquetas</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                  placeholder="vip"
                  className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:border-emerald-500 focus:outline-none"
                />
                <button type="button" onClick={addTag} className="inline-flex items-center justify-center rounded-lg border border-border bg-card hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))] disabled:opacity-50 px-3 py-2 text-sm">Agregar</button>
              </div>
              <div className="flex gap-1 flex-wrap mt-2">
                {(form.tags || []).map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-xs text-foreground">
                    {t}
                    <button type="button" onClick={() => removeTag(t)} className="text-muted-foreground hover:text-foreground">
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="c_optedin"
                checked={!!form.opted_in}
                onChange={(e) => setForm((f) => ({ ...f, opted_in: e.target.checked }))}
                className="w-4 h-4 rounded border-border bg-background wa-accent-text focus:ring-emerald-500"
              />
              <label htmlFor="c_optedin" className="text-sm text-foreground">
                Aceptó recibir promociones (se guarda la fecha)
              </label>
            </div>
            <div className="flex justify-end gap-3 pt-3 border-t border-border">
              <button type="button" onClick={onClose} className="inline-flex items-center justify-center rounded-lg border border-border bg-card hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))] disabled:opacity-50 px-3 py-2 text-sm">Cancelar</button>
              <button type="submit" disabled={saving} className="wa-btn inline-flex items-center justify-center rounded-lg font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))] focus-visible:ring-offset-2 focus-visible:ring-offset-background px-3 py-2 text-sm flex items-center gap-2">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                Crear
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default WhatsAppContactsPage;
